import { Link } from "@tanstack/react-router";
import { Logo } from "./logo";
import { trackEvent } from "@/lib/analytics";
import { Mail, Phone, MapPin, Twitter, Linkedin, Github, Facebook, Instagram, Youtube, MessageSquare, Bitcoin, Landmark, Smartphone, Wallet, Star, MessageCircle } from "lucide-react";
import { useSiteSettings } from "@/hooks/use-site-settings";
import type { FooterSettings } from "@/routes/_authenticated/admin/footer";

const SOCIAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Twitter, LinkedIn: Linkedin, GitHub: Github, Facebook, Instagram,
  YouTube: Youtube, TikTok: MessageSquare, WhatsApp: MessageCircle,
};
type Pay = {
  name: string;
  label: string;
  bg: string;
  fg: string;
  icon?: React.ComponentType<{ className?: string }>;
  font?: string;
};

const PAYMENTS: Pay[] = [
  { name: "Visa",     label: "VISA",     bg: "bg-[#1A1F71]", fg: "text-white", font: "italic font-extrabold tracking-wider" },
  { name: "Master",   label: "Master",   bg: "bg-[#0A0A0A]", fg: "text-white", font: "font-bold" },
  { name: "PayPal",   label: "PayPal",   bg: "bg-[#003087]", fg: "text-white", font: "italic font-bold" },
  { name: "Payoneer", label: "Payoneer", bg: "bg-[#D93800]", fg: "text-white", font: "font-bold" },
  { name: "STC Pay",  label: "STC Pay",  bg: "bg-[#4F008C]", fg: "text-white", font: "font-bold" },
  { name: "bKash",    label: "bKash",    bg: "bg-[#B70054]", fg: "text-white", font: "font-bold", icon: Smartphone },
  { name: "Bank",     label: "Bank",     bg: "bg-[#0B6654]", fg: "text-white", font: "font-semibold", icon: Landmark },
  { name: "Crypto",   label: "Crypto",   bg: "bg-[#F7931A]", fg: "text-[#0f172a]", font: "font-bold", icon: Bitcoin },
  { name: "Wallet",   label: "Wallet",   bg: "bg-[#1e293b]", fg: "text-white", font: "font-semibold", icon: Wallet },
];

function PaymentBadges() {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {PAYMENTS.map((p) => {
        const Icon = p.icon;
        return (
          <span
            key={p.name}
            title={`We accept ${p.name}`}
            className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11px] shadow-sm ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-glow ${p.bg} ${p.fg} ${p.font ?? ""}`}
          >
            {Icon && <Icon className="h-3 w-3" />}
            <span className="leading-none">{p.label}</span>
          </span>
        );
      })}
    </div>
  );
}

const cols = [
  {
    title: "Services",
    links: [
      ["Web Development", "/services"],
      ["Mobile Apps", "/services"],
      ["SaaS Development", "/services"],
      ["AI Automation", "/services"],
      ["UI / UX Design", "/services"],
      ["E-commerce", "/services"],
      ["DevOps", "/services"],
      ["API Integration", "/services"],
      ["Maintenance", "/services"],
    ],
  },
  {
    title: "Software",
    links: [
      ["ERP", "/software"],
      ["POS", "/software"],
      ["CRM", "/software"],
      ["HRM", "/software"],
      ["LMS", "/software"],
      ["Accounting", "/software"],
      ["Inventory", "/software"],
      ["Booking", "/software"],
      ["Marketplace", "/marketplace"],
    ],
  },
  {
    title: "Hosting",
    links: [
      ["Shared Hosting", "/hosting"],
      ["VPS", "/hosting"],
      ["Cloud Hosting", "/hosting"],
      ["Dedicated Server", "/hosting"],
      ["WordPress Hosting", "/hosting"],
      ["Reseller Hosting", "/hosting"],
      ["SSL", "/hosting"],
      ["Domains", "/domains"],
      ["WHOIS", "/domains"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About", "/about"],
      ["Portfolio", "/portfolio"],
      ["Blog", "/blog"],
      ["Careers", "/about"],
      ["Partners", "/about"],
      ["Press", "/blog"],
      ["Pricing", "/services"],
      ["Support", "/support"],
      ["Contact", "/contact"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy Policy", "/privacy"],
      ["Terms of Service", "/terms"],
      ["Cookie Policy", "/cookies"],
      ["Refund Policy", "/refund"],
      ["SLA", "/sla"],
      ["Acceptable Use", "/terms"],
      ["GDPR", "/privacy"],
      ["DMCA", "/terms"],
      ["Disclaimer", "/terms"],
    ],
  },
] as const;

export function Footer() {
  const { data } = useSiteSettings<Partial<FooterSettings>>("footer");

  const tagline = data?.tagline ?? "CoderLity is a full-service digital agency building software, SaaS, ERP, POS and growth systems for ambitious brands worldwide.";
  const email = data?.email ?? "hello@coderlity.com";
  const phone = data?.phone ?? "+966 55 975 8082";
  const address = data?.address ?? "Riyadh & Jeddah, KSA · CO Bazar, Rangpur, BD";
  const columns = (data?.columns && data.columns.length > 0)
    ? data.columns
    : cols.map((c) => ({ title: c.title, links: c.links.map(([label, to]) => ({ label, to })) }));
  const socials = data?.socials && data.socials.length > 0
    ? data.socials
    : [
        { kind: "Twitter", url: "#" },
        { kind: "LinkedIn", url: "#" },
        { kind: "GitHub", url: "#" },
        { kind: "Facebook", url: "#" },
        { kind: "Instagram", url: "#" },
      ];
  const copyright = (data?.copyright ?? "© 2022–{year} All Rights Reserved · Developed by CoderLity")
    .replace("{year}", String(new Date().getFullYear()));

  return (
    <footer className="relative mt-32 border-t border-border bg-card/40">
      <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 lg:px-8 py-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4 space-y-4">
            <Logo />
            <p className="text-sm text-muted-foreground max-w-sm">{tagline}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><span>{email}</span></li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /><span>{phone}</span></li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-primary mt-0.5" /><span>{address}</span></li>
            </ul>
            <div className="flex gap-2 pt-2">
              {socials.map(({ kind, url }) => {
                const Icon = SOCIAL_ICONS[kind] ?? MessageCircle;
                return (
                  <a
                    key={kind + url}
                    href={url || "#"}
                    aria-label={kind}
                    target={url && url !== "#" ? "_blank" : undefined}
                    rel="noreferrer"
                    className="group relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/50 text-muted-foreground transition-all duration-300 hover:-translate-y-0.5 hover:scale-110 hover:border-primary/60 hover:text-primary-foreground hover:shadow-glow overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <span className="absolute inset-0 bg-gradient-brand opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <Icon className="relative h-4 w-4 transition-transform duration-300 group-hover:rotate-6" />
                  </a>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 pt-3">
              <a
                href="https://trustreview.net/businesses/coderlity"
                target="_blank"
                rel="noreferrer"
                aria-label="Trust Review"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-xs font-semibold text-foreground/90 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-glow"
              >
                <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                Trust Review
              </a>
              <a
                href="https://www.trustpilot.com/review/coderlity.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Trustpilot"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-xs font-semibold text-foreground/90 shadow-sm transition hover:-translate-y-0.5 hover:border-[#00b67a]/60 hover:shadow-glow"
              >
                <Star className="h-3.5 w-3.5 fill-[#00b67a] text-[#00b67a]" />
                Trustpilot
              </a>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8">
            {columns.map((c) => (
              <div key={c.title} className="space-y-1">
                <div className="text-sm font-semibold mb-3 pb-2 border-b border-border/60 text-foreground">{c.title}</div>
                <ul className="space-y-1.5">
                  {c.links.map((lnk) => (
                    <li key={lnk.label + lnk.to}>
                      <a
                        href={lnk.to}
                        className="group relative inline-flex min-h-[44px] sm:min-h-0 items-center gap-1.5 rounded-md px-2.5 py-2 text-sm text-muted-foreground transition-all duration-200 hover:bg-primary/10 hover:text-primary hover:pl-3"
                      >
                        <span className="h-1 w-1 rounded-full bg-primary/50 opacity-0 transition-opacity group-hover:opacity-100" />
                        {lnk.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* We Accept */}
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            We Accept
          </div>
          <PaymentBadges />
        </div>
      </div>

      <div className="relative border-t border-border overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-[-0.15em] select-none text-center font-display font-bold tracking-tight leading-none text-[18vw] bg-gradient-brand bg-clip-text text-transparent opacity-10"
        >
          CoderLity
        </div>
        <div className="relative mx-auto max-w-7xl px-4 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-muted-foreground">{copyright}</div>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => { trackEvent("chat_widget_opened", { source: "footer_button" }); window.dispatchEvent(new CustomEvent("open-chat")); }}
              aria-label="Open support chat"
              aria-haspopup="dialog"
              aria-controls="coderlity-chat-dialog"
              className="inline-flex items-center gap-1.5 rounded-full bg-gradient-brand text-white px-3 py-1.5 text-xs font-semibold shadow-glow transition hover:scale-[1.03]"
            >
              <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
              Live Chat
            </button>
            <Link to="/privacy" className="text-xs text-muted-foreground transition hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="text-xs text-muted-foreground transition hover:text-foreground">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
